import axios from 'axios'
import * as Sentry from '@sentry/react'
import { AUTH_GOOGLE_VERIFY_PATH } from '../lib/authGooglePath'
import {
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
} from '../lib/authTokenStorage'

export const CHATIZI_API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:8000'

const REFRESH_URL_PATH = '/manager/auth/refresh'

let isRefreshing = false
let failedQueue = []

function processQueue(error, token = null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error)
    else p.resolve(token)
  })
  failedQueue = []
}

function shouldSkip401Refresh(config) {
  const url = config?.url ?? ''
  return (
    url.includes('/manager/auth/login') ||
    url.includes('/manager/auth/refresh') ||
    url.includes('/manager/auth/send-otp') ||
    url.includes('/manager/auth/signup') ||
    url.includes('/manager/auth/forgot-password') ||
    url.includes('/manager/auth/reset-password') ||
    url.includes('/admin/auth/login') ||
    url.includes('/admin/auth/refresh') ||
    url.includes('/admin/auth/send-otp') ||
    url.includes('/admin/auth/signup') ||
    url.includes('/admin/auth/forgot-password') ||
    url.includes('/admin/auth/reset-password') ||
    url.includes(AUTH_GOOGLE_VERIFY_PATH)
  )
}

/**
 * Single shared refresh queue for all axios instances (main API + speakbot JWT).
 * Attaches Bearer token from storage on each request and handles 401 refresh.
 */
export function attachAuthRefreshInterceptor(axiosInstance, apiBaseUrl) {
  axiosInstance.interceptors.request.use((config) => {
    const token = getAuthToken()
    if (token) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error.config
      const status = error.response?.status

      if (status !== 401 || !originalRequest || shouldSkip401Refresh(originalRequest)) {
        return Promise.reject(error)
      }

      if (originalRequest._retry) {
        Sentry.addBreadcrumb({
          message: 'Token refresh retry failed — forcing logout',
          level: 'warning',
          data: { url: originalRequest.url },
        })
        window.dispatchEvent(new Event('auth:logout'))
        return Promise.reject(error)
      }

      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        Sentry.addBreadcrumb({
          message: 'No refresh token available — forcing logout',
          level: 'warning',
          data: { url: originalRequest.url },
        })
        window.dispatchEvent(new Event('auth:logout'))
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${token}`
            originalRequest._retry = true
            return axiosInstance(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      isRefreshing = true
      try {
        const { data } = await axios.post(
          `${apiBaseUrl}${REFRESH_URL_PATH}`,
          { refresh_token: refreshToken },
          {
            timeout: 15000,
            headers: { 'Content-Type': 'application/json' },
          }
        )
        const accessToken = data.access_token
        const newRefresh = data.refresh_token
        setAuthToken(accessToken)
        setRefreshToken(newRefresh)
        window.dispatchEvent(
          new CustomEvent('auth:token-refreshed', {
            detail: { access_token: accessToken },
          })
        )

        isRefreshing = false
        processQueue(null, accessToken)

        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        originalRequest._retry = true
        return axiosInstance(originalRequest)
      } catch (refreshErr) {
        isRefreshing = false
        processQueue(refreshErr, null)
        Sentry.captureException(refreshErr, {
          tags: { module: 'auth', action: 'token_refresh' },
          extras: { triggering_url: originalRequest?.url },
        })
        window.dispatchEvent(new Event('auth:logout'))
        return Promise.reject(refreshErr)
      }
    }
  )
}
